import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { authAPI, User } from '../api/authApi';
import { clearPermissionCache } from '../acl/usePermission';
import { AuthErrorDialog } from './AuthErrorDialog';
import { setAuthErrorCallback } from './authErrorHandler';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    authenticated: boolean;
    signIn: (email: string) => Promise<{ success: boolean; message: string }>;
    signUp: (email: string) => Promise<{ success: boolean; message: string }>;
    signOut: () => Promise<void>;
    refreshUser: () => Promise<void>;
    showAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [showAuthErrorDialog, setShowAuthErrorDialog] = useState(false);

    const refreshUser = useCallback(async () => {
        try {
            const currentUser = await authAPI.getCurrentUser();
            setUser(currentUser);
        } catch (error) {
            console.error('Error refreshing user:', error);
            setUser(null);
        }
    }, []);

    const signIn = async (email: string): Promise<{ success: boolean; message: string }> => {
        console.log('🔐 AuthContext: Initiating passwordless login...');
        const result = await authAPI.loginInitiate(email);
        // Login verification happens via magic link, so user state stays unchanged here
        return result;
    };

    const signUp = async (email: string): Promise<{ success: boolean; message: string }> => {
        console.log('🔐 AuthContext: Initiating passwordless registration...');
        const result = await authAPI.registerInitiate(email);
        return result;
    };

    const signOut = async (): Promise<void> => {
        await authAPI.signOut();
        clearPermissionCache();
        setUser(null);
    };

    useEffect(() => {
        const checkAuthStatus = async () => {
            try {
                console.log('🔐 AuthContext: Checking auth status on mount...');
                // Support both 'authToken' and 'idToken' keys during migration
                const token = authAPI.getIdToken();
                console.log('🔐 AuthContext: Token from storage:', token ? `Found (length: ${token.length})` : 'Not found');

                if (token) {
                    // Token exists, try to get user data
                    try {
                        console.log('🔐 AuthContext: Token found, fetching user data...');
                        const userData = await authAPI.getCurrentUser();
                        console.log('✅ AuthContext: Got user data', userData);
                        setUser(userData);
                    } catch (error: any) {
                        console.error('❌ AuthContext: Error getting user data:', error);
                        // Only clear token if it's explicitly an authentication error
                        // Don't clear on network errors or other non-auth errors
                        const errorMessage = error?.message || error?.toString() || '';
                        const isAuthError = errorMessage.includes('401') ||
                            errorMessage.includes('Authentication') ||
                            errorMessage.includes('unauthorized');

                        if (isAuthError) {
                            console.log('❌ AuthContext: Authentication error detected, clearing token');
                            console.log('❌ AuthContext: Error message:', errorMessage);
                            console.trace('🔍 AuthContext: Token cleared due to auth error in checkAuthStatus');
                            await authAPI.signOut();
                            setUser(null);
                            setShowAuthErrorDialog(true);
                        } else {
                            // For non-auth errors, keep the token but set user to null
                            // The token might still be valid, just couldn't fetch user data
                            console.log('⚠️ AuthContext: Non-auth error, keeping token but clearing user state');
                            setUser(null);
                        }
                    }
                } else {
                    // No token, user is not authenticated
                    console.log('🔐 AuthContext: No token found in storage');
                    setUser(null);
                }
            } catch (error) {
                console.error('❌ AuthContext: Error checking auth status:', error);
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        checkAuthStatus();
    }, []);

    useEffect(() => {
        setAuthErrorCallback(() => {
            clearPermissionCache();
            setShowAuthErrorDialog(true);
            setUser(null);
            authAPI.signOut();
            window.location.href = '/login';
        });
    }, []);

    const showAuthError = () => {
        setShowAuthErrorDialog(true);
    };

    const value: AuthContextType = {
        user,
        loading,
        authenticated: !!user,
        signIn,
        signUp,
        signOut,
        refreshUser,
        showAuthError,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
            <AuthErrorDialog
                open={showAuthErrorDialog}
                onOpenChange={setShowAuthErrorDialog}
            />
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
